
export enum MediaTypeEnum {
  image = 'image',
  video = 'video',
  youtube = 'youtube',
}

export enum ExerciseTypesEnum {
  mobility = 'mobility',
  stretching = 'stretching',
  weight = 'weight',
  cardio = 'cardio',
}

export enum AspectRatioEnum {
  portrait = 'portrait',
  landscape = 'landscape',
}

export interface BaseMedia {
  type: MediaTypeEnum;
}

export interface StaticMedia extends BaseMedia {
  type: MediaTypeEnum.image;
  src?: string, // legacy parameter
  mediaUUID: string;
}

export interface VideoMedia extends BaseMedia {
  type: MediaTypeEnum.video;
  src?: string, // legacy parameter
  mediaUUID: string;
}


export interface YouTubeMedia extends BaseMedia {
  type: MediaTypeEnum.youtube;
  mediaUUID: string;
  // id: string;
  // aspect_ratio: AspectRatioEnum;
}

export type Media = StaticMedia | VideoMedia | YouTubeMedia;

export interface ExecutionModel {
  series: string[];
  pause?: [number, number];
  cadence?: [number, number];
}

export interface ExerciseModel {
  name: string;
  type: ExerciseTypesEnum;
  execution: ExecutionModel;
  observations?: string;
  media: Media[];
}

export interface TrainingDayModel {
  name: string;
  exercises: ExerciseModel[];
}


export type WorkoutId = string; // in reality it should be a UUID

// version history
// version 1 the initial datamodel for starting from the beta launch
// version 2 Changed the ExecutionModel
// version 3 Exercise combinations (block/slot/phase). Data design of record:
//           new-features/exercise_combination_data_design_v3_1.md (aligned to UI v10).
//           Migration is read-time wrap + write-through; the v3 shapes below stay inert
//           until the version guard + SW fleet turn (design §4.4).

export interface WorkoutModelDB {
  version: number; // 1
  planUUID: string;
  userUUID?: string;
  workoutName: string;
  objective: string;
  startDate: Date;
  endDate: Date;
  workouts: TrainingDayModel[];
}

export type SeriesModel = {
  type: 'single' | 'multiple' | 'error';
  desc: string;
  unit?: 's' | 'min';
  noSeries: number;
  noRepetitions: readonly [number | undefined, number];
}
export interface ExecutionModelv2 {
  series: SeriesModel[]
  pause?: [number, number];
  cadence?: [number, number];
}

export interface ExerciseModelv2 {
  name: string;
  type: ExerciseTypesEnum;
  execution: ExecutionModelv2;
  observations?: string;
  media: Media[];
}

export interface TrainingDayModelv2 {
  name: string;
  exercises: ExerciseModelv2[];
}

export interface WorkoutModelDBv2 {
  version: number; // 2
  planUUID: string;
  userUUID?: string;
  workoutName: string;
  objective: string;
  startDate: Date;
  endDate: Date;
  published?: Date;
  workouts: TrainingDayModelv2[];
}

// ============================================================================
// Version 3 — exercise combinations (data design of record:
// new-features/exercise_combination_data_design_v3_1.md, aligned to UI v10).
//
// Vocabulary of record: kinds solo | superset | complex; UI "Tempo" = cadence
// (string), "Inter-exercise break" = slot.execution.pause, "Inter-set break" =
// block.execution.roundPause, "Sets" (combos) = execution.rounds.
// Phases are stored trainer-owned data (v9/v10) — the old WorkoutPhasesModel
// type-bucket round-trip below remains the v2 editor working model only.
//
// These shapes stay INERT until the version guard + SW fleet turn (design §4.4):
// a deployed v2 client must never see a version: 3 payload.
// ============================================================================

export enum CombinationKind {
  solo = 'solo',      // 1 slot — today's exercise, unchanged semantics
  superset = 'superset', // A → break → B (…→ N); own weights; break row inside the round
  complex = 'complex',   // no breaks, one shared load; default at 3+ slots (v10)
}

// Cosmetic tokens, never hex (design §2 structural rules)
export type PhaseColor = 'blue' | 'green' | 'yellow' | 'red' | 'plain';

export interface PhaseModel {
  name: string;            // trainer-owned free text ("Warm-up", "Mobility", anything)
  color?: PhaseColor;      // purely cosmetic (v10 Q2); client view de-emphasises it
  blocks: BlockModel[];    // a block belongs to exactly one phase (v10 Q3)
}

export interface ExecutionModelv3 {
  // solo: ramps allowed; superset/complex: exactly ONE entry (structural invariant §2)
  series: SeriesModel[];
  pause?: [number, number]; // UI "Inter-exercise break" — the break BEFORE this slot; ignored on slot A
}

export interface SlotModel {
  exercise: string;        // display name; exercise-library UUID stays Phase D
  type: ExerciseTypesEnum; // per slot — a superset may mix weight + cardio
  media: Media[];
  execution: ExecutionModelv3;
  cadence?: string;        // UI "Tempo", e.g. "3-1-X-0" (string now — design §1.4)
  observations?: string;   // per-slot note
}

export interface BlockModel {
  kind: CombinationKind;
  name?: string;           // "The Grind", "Bench × Flyes"
  instructions?: string;   // v5 coach card: shown verbatim in the client Coach's notes
  sharedLoad?: boolean;    // complex: one implement, one weight fans into every slot's sets;
                           // meaningless for superset, defaults true for complex
  execution: {
    rounds: number;        // UI "Sets" stepper; ignored for solo (series ramps loop, as today)
    roundPause?: [number, number]; // UI "Inter-set break" — the band between round groups
  };
  slots: SlotModel[];      // length 1 = solo; ≥ 2 combinations (UI soft cap ~6)
}

export interface TrainingDayModelv3 {
  name: string;
  phases: PhaseModel[];    // was: exercises (derived buckets become stored reality)
}

export interface WorkoutModelDBv3 {
  version: 3;
  planUUID: string;
  userUUID?: string;
  workoutName: string;
  objective: string;
  startDate: Date;
  endDate: Date;
  published?: Date;
  workouts: TrainingDayModelv3[];
}

/**
 * Group items into [key, items] buckets in FIRST-APPEARANCE order, preserving the
 * original order inside each bucket. This is THE shared transform for the v2→v3
 * migration window (design §4.3): plan order and log order must move by the exact
 * same rule, or carry-forward lands on the wrong block. Both migrationTo*V3 paths
 * import it from here — never re-implement it.
 */
export function stablePartitionBy<T, K>(items: readonly T[], key: (item: T) => K): [K, T[]][] {
  const order: K[] = [];
  const buckets = new Map<K, T[]>();
  for (const item of items) {
    const k = key(item);
    let bucket = buckets.get(k);
    if (!bucket) {
      bucket = [];
      buckets.set(k, bucket);
      order.push(k);
    }
    bucket.push(item);
  }
  return order.map((k) => [k, buckets.get(k)!]);
}

/**
 * Flatten a v3 day back to the positional contract: phases in stored order,
 * blocks in phase order. This is the index space log[]/exercises[] live in
 * (route /plan/:planUUID/:workoutId/:exerciseId = flattened block index).
 */
export function flattenDayBlocks(day: TrainingDayModelv3): BlockModel[] {
  return day.phases.flatMap((phase) => phase.blocks);
}

/**
 * Migrate a legacy numeric cadence pair (a field no shipped UI ever wrote) to the
 * v3 Tempo string. Strings pass through untouched.
 */
export function stringifyCadence(cadence?: [number, number] | string): string | undefined {
  if (cadence === undefined) {
    return undefined;
  }
  if (typeof cadence === 'string') {
    return cadence;
  }
  return cadence.join('-');
}

/**
 * Default phase colour when migrating a derived type-bucket into a stored phase
 * (matches the v2 editor header accents, tfp-trainer-plan-admin-page.ts:291-297).
 */
export function defaultColorFor(type: ExerciseTypesEnum): PhaseColor {
  switch (type) {
    case ExerciseTypesEnum.mobility:
      return 'blue';
    case ExerciseTypesEnum.stretching:
      return 'yellow';
    case ExerciseTypesEnum.cardio:
      return 'red';
    case ExerciseTypesEnum.weight:
    default:
      return 'green';
  }
}

export interface WorkoutPhasesModel {
  type: ExerciseTypesEnum;
  exercises: ExerciseModelv2[];
}

export interface WorkoutEditModel {
  name: string;
  phases: WorkoutPhasesModel[];
}
